/**
 * Service for Rebrickable authentication and credentials management.
 * Handles user token generation and credential storage.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * Get a Rebrickable user token by authenticating with username and password.
 * Calls Rebrickable API directly from the browser - the password never touches our servers.
 *
 * @param username Rebrickable username or email
 * @param password Rebrickable password
 * @param apiKey Rebrickable API key (required for authentication)
 * @returns User token from Rebrickable
 * @throws Error if authentication fails
 */
export async function getUserToken(
  username: string,
  password: string,
  apiKey: string
): Promise<string> {
  // Call Rebrickable API directly from browser
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);

  const response = await fetch('https://rebrickable.com/api/v3/users/_token/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `key ${apiKey}`
    },
    body: formData
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403 || response.status === 401) {
      throw new Error('Invalid username or password. Please verify your credentials at https://rebrickable.com');
    }
    throw new Error(errorData.detail || 'Failed to obtain user token from Rebrickable');
  }

  const data = await response.json();
  return data.user_token;
}

/**
 * Save Rebrickable credentials (API key and user token) to user settings.
 *
 * @param apiKey Rebrickable API key
 * @param userToken Rebrickable user token
 * @param token JWT token for authentication
 * @throws Error if save fails
 */
export async function saveCredentials(
  apiKey: string,
  userToken: string,
  token: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      settings: {
        rebrickable_api_key: apiKey,
        rebrickable_user_token: userToken
      }
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to save credentials');
  }
}

/**
 * Get user profile from Rebrickable to verify the user token is valid.
 *
 * @param userToken Rebrickable user token
 * @param apiKey Rebrickable API key
 * @returns User profile with username and other details
 * @throws Error if profile fetch fails
 */
export async function getUserProfile(
  userToken: string,
  apiKey: string
): Promise<{
  username: string;
  user_hash: string;
}> {
  const response = await fetch(`https://rebrickable.com/api/v3/users/${userToken}/profile/`, {
    headers: {
      'Authorization': `key ${apiKey}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to fetch user profile from Rebrickable');
  }

  return response.json();
}

/**
 * Check if the user already has Rebrickable credentials saved.
 *
 * @param token JWT token for authentication
 * @returns True if user has both API key and user token saved
 * @throws Error if check fails
 */
export async function checkExistingCredentials(token: string): Promise<{
  hasApiKey: boolean;
  hasUserToken: boolean;
  hasBoth: boolean;
}> {
  const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to check existing credentials');
  }

  const data = await response.json();
  const settings = data.settings || [];

  const apiKeySetting = settings.find((s: { key: string; value: string }) => s.key === 'rebrickable_api_key');
  const userTokenSetting = settings.find((s: { key: string; value: string }) => s.key === 'rebrickable_user_token');

  const hasApiKey = apiKeySetting && apiKeySetting.value && apiKeySetting.value !== '';
  const hasUserToken = userTokenSetting && userTokenSetting.value && userTokenSetting.value !== '';

  return {
    hasApiKey,
    hasUserToken,
    hasBoth: hasApiKey && hasUserToken
  };
}
